"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TranslationProvider = exports.defaultWildcardFallbacks = exports.defaultFallbacks = void 0;
var tslib_1 = require("tslib");
/* eslint-disable import/no-named-as-default-member */
var i18next_1 = tslib_1.__importDefault(require("i18next"));
var i18next_browser_languagedetector_1 = tslib_1.__importDefault(require("i18next-browser-languagedetector"));
var i18next_chained_backend_1 = tslib_1.__importDefault(require("i18next-chained-backend"));
var i18next_resources_to_backend_1 = tslib_1.__importDefault(require("i18next-resources-to-backend"));
var react_1 = tslib_1.__importStar(require("react"));
var react_i18next_1 = require("react-i18next");
exports.defaultFallbacks = {
    de: ['de-DE', 'en'],
    ja: ['ja-JP', 'en'],
    zh: ['zh-CN', 'en'],
    'zh-CN': ['zh-SG', 'en'],
    'zh-TW': ['zh-HK', 'zh-MO', 'en'],
    nl: ['nl-NL', 'en'],
    'nl-BE': ['nl-NL', 'en'],
    fr: ['fr-FR', 'en'],
    it: ['it-IT', 'en'],
    ko: ['ko-KR', 'en'],
    pl: ['pl-PL', 'en'],
    pt: ['pt-BR', 'en'],
    es: ['es-MX', 'es-419', 'en'],
    default: ['en'],
};
exports.defaultWildcardFallbacks = ['en', 'de', 'fr', 'it', 'pt', 'es'];
/*
 * This is here to constrain the locales that come from `Accept-Language` headers. Whilst all code appears to support
 * this set of locales, there are some that support a much broader set. If we are completely open with the locales
 * when they are not specified, then we could end up with a mixed UI, e.g. Chinese & English. So this is the set of
 * locales that are to be supported during Q2.
 */
var defaultSupportedLngs = [
    'de-DE',
    'en-US',
    'es-419',
    'fr-FR',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'nl-NL',
    'pl-PL',
    'pt-BR',
];
var getSupportedLngs = function (locales) {
    // If there are no locales specified use the default supported set
    var localesToUse = locales && Array.isArray(locales) ? locales : defaultSupportedLngs;
    // Ensure four-letter locale codes include a two-letter fallback
    return (localesToUse
        .flatMap(function (code) { return [code, code.substring(0, 2)]; })
        // Remove duplicates in case the user has already defined
        // the two-letter code in the `locales` array
        .filter(function (code, i, self) { return self.indexOf(code) === i; }));
};
var TranslationProvider = function (_a) {
    var children = _a.children, getLocale = _a.getLocale, locale = _a.locale, _b = _a.fallback, fallback = _b === void 0 ? null : _b, _c = _a.useSuspense, useSuspense = _c === void 0 ? true : _c, _d = _a.fallbacks, fallbacks = _d === void 0 ? exports.defaultFallbacks : _d, reactiveMapLocale = _a.reactiveMapLocale, supportedLocales = _a.supportedLocales, _e = _a.configOverride, configOverride = _e === void 0 ? {} : _e, _f = _a.wildcardFallbacks, wildcardFallbacks = _f === void 0 ? exports.defaultWildcardFallbacks : _f;
    var lng = locale;
    if (reactiveMapLocale) {
        lng = reactiveMapLocale.useReadSharedState(reactiveMapLocale.key);
    }
    var i18nextInstance = (0, react_1.useMemo)(function () {
        var newInstance = i18next_1.default
            .createInstance()
            .use(i18next_browser_languagedetector_1.default) // See if cookie etc. already exists to get the locale
            .use(i18next_chained_backend_1.default)
            .use(react_i18next_1.initReactI18next);
        newInstance.init(tslib_1.__assign({ lng: lng, supportedLngs: getSupportedLngs(supportedLocales), interpolation: { escapeValue: false }, fallbackLng: function (code) {
                var _a;
                // We support wildcard fallback for these languages (eg. de-AT / de-CH can fallback to de-DE)
                var wildcardMatch = wildcardFallbacks.find(function (locale) {
                    return locale.startsWith("".concat(code, "-"));
                });
                return tslib_1.__assign(tslib_1.__assign({}, fallbacks), (wildcardMatch ? (_a = {}, _a[code] = [wildcardMatch, 'en'], _a) : {}));
            }, detection: {
                // Persist language selection in a cookie so that when we
                // navigate across subdomains the selected locale is available
                caches: ['localStorage', 'cookie'],
                lookupCookie: 'i18next',
                cookieDomain: '.hopin.com',
                cookieOptions: {
                    path: '/',
                    domain: '.hopin.com',
                    expires: new Date('Fri, 31 Dec 9999 23:59:59 GMT'),
                },
            }, backend: {
                backends: [
                    (0, i18next_resources_to_backend_1.default)(function (locale, _, //namespace, unused
                    callback) { return tslib_1.__awaiter(void 0, void 0, void 0, function () {
                        var newLocale, error_1;
                        return tslib_1.__generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    _a.trys.push([0, 2, , 3]);
                                    return [4 /*yield*/, getLocale(locale)];
                                case 1:
                                    newLocale = _a.sent();
                                    callback(null, newLocale.default);
                                    return [3 /*break*/, 3];
                                case 2:
                                    error_1 = _a.sent();
                                    callback(error_1, null);
                                    return [3 /*break*/, 3];
                                case 3: return [2 /*return*/];
                            }
                        });
                    }); }),
                ],
            } }, configOverride));
        return newInstance;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    (0, react_1.useEffect)(function () {
        if (i18nextInstance.language !== lng) {
            i18nextInstance.changeLanguage(lng);
        }
    }, [i18nextInstance, lng]);
    if (useSuspense) {
        return (react_1.default.createElement(react_1.Suspense, { fallback: fallback },
            react_1.default.createElement(react_i18next_1.I18nextProvider, { i18n: i18nextInstance }, children)));
    }
    return react_1.default.createElement(react_i18next_1.I18nextProvider, { i18n: i18nextInstance }, children);
};
exports.TranslationProvider = TranslationProvider;
//# sourceMappingURL=TranslationProvider.js.map